import type { Renderer2 } from '@angular/core';
import { DOCUMENT, RendererFactory2, Service, inject } from '@angular/core';
import { OnyxToastMessage } from '../components/toast/enums';
import type { OnyxPhone } from '../interfaces';
import { OnyxToastService } from '../services/onyx-toast.service';

@Service()
export class ActionHelper {
  private readonly toastService = inject(OnyxToastService);
  private readonly document = inject(DOCUMENT);
  private readonly rendererFactory = inject(RendererFactory2);

  private readonly renderer: Renderer2;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  public copy(text: string): void {
    ActionHelper.copy(text, this.toastService);
  }

  public share(route: string): void {
    ActionHelper.share(route, this.toastService);
  }

  public saveFile(
    content: string,
    contentType: string,
    fileName: string,
  ): void {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);

    const linkElement = this.renderer.createElement('a');
    this.renderer.setAttribute(linkElement, 'href', url);
    this.renderer.setAttribute(linkElement, 'download', fileName);

    this.renderer.appendChild(this.document.body, linkElement);
    linkElement.click();
    this.renderer.removeChild(this.document.body, linkElement);

    URL.revokeObjectURL(url);
  }

  public downloadFile(file: File): void {
    const url = URL.createObjectURL(file);

    const linkElement = this.renderer.createElement('a');
    this.renderer.setAttribute(linkElement, 'href', url);
    this.renderer.setAttribute(linkElement, 'download', file.name);

    this.renderer.appendChild(this.document.body, linkElement);
    linkElement.click();
    this.renderer.removeChild(this.document.body, linkElement);

    URL.revokeObjectURL(url);
  }

  public static copy(
    text: string,
    toastService: OnyxToastService | false,
  ): void {
    navigator.clipboard.writeText(text);
    if (!toastService) return;

    toastService.showCustom(OnyxToastMessage.COPIED_TO_CLIPBOARD);
  }

  public static share(route: string, toastService: OnyxToastService): void {
    const url = `${window.location.origin}${route}`;
    navigator.clipboard.writeText(url);
    toastService.showCustom(OnyxToastMessage.COPIED_TO_CLIPBOARD);
  }

  public static makeCall(phone: OnyxPhone | null): void {
    if (!phone) return;
    window.open(`tel:${phone.areaCode}${phone.number}`, '_self');
  }

  public static openRoute(route: string): void {
    const url = `${window.location.origin}${route}`;
    window.open(url, '_blank');
  }

  public static openLink(link: string): void {
    window.open(link, '_blank');
  }

  public static sendEmail(email: string): void {
    window.open(`mailto:${email}`, '_self');
  }
}
